import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getAsset } from '../../../../../../../../../util/assets';
import Badge from '../../../../../../../../../components/global/Badge';
import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import Button from '../../../../../../../../../components/global/Button';
import AddedToCartLabel from '../../../PhotoPurchasingOptions/AddedToCartLabel';
import {
  getIdentityCallingName,
  isAlbumCustomPrintConfigurableInPlace,
} from '../../helpers';
import {
  getAlbumCustomPrintName,
  getAlbumCustomPrintPrice,
} from '../../../../../../../../../util/custom-prints';
import { getAlbumFolderSubscriptionCartItemPrice } from '../../../../../../../../../services/checkout/redux/helpers';
import AlbumCustomPrintSubscriptionPreviewModal from './AlbumCustomPrintSubscriptionPreviewModal';
import ImagePreload from '../../../../../../../../../components/global/ImagePreload';
import { validateCustomPrintPhotoSelections } from '../../../../../../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/helpers';
import CustomPrintPlaqueConfiguratorModal from '../../../../../../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/modal';

const propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  inCart: PropTypes.bool,
  albumCustomPrint: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  subscriptionItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  onBundleToggle: PropTypes.func.isRequired,
};

const AlbumCustomPrintSubscriptionBundleOption = ({
  className,
  readOnly,
  albumCustomPrint,
  subscriptionItem,
  identity,
  inCart,
  onBundleToggle,
}) => {
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-album-custom-print-bundle': true,
  });
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [configurationModalOPen, setConfigurationModalOpen] = useState(false);

  const name = identity ? getIdentityCallingName(identity) : '';
  const albumCustomPrintName = getAlbumCustomPrintName(albumCustomPrint);

  const albumCustomPrintPrice = getAlbumCustomPrintPrice(albumCustomPrint);
  const albumCustomPrintDiscountPrice =
    getAlbumCustomPrintPrice(albumCustomPrint, true) ?? albumCustomPrintPrice;
  const subscriptionPrice =
    getAlbumFolderSubscriptionCartItemPrice({ albumFolder: subscriptionItem })
      ?.value || subscriptionItem.price_whole_album?.value;
  const oldPrice = albumCustomPrintPrice + subscriptionPrice;
  const finalPrice = albumCustomPrintDiscountPrice + subscriptionPrice;
  const albumCustomPrintHasPreview =
    !!albumCustomPrint.custom_print.preview_image;

  const onBundleRequest = () => {
    if (isAlbumCustomPrintConfigurableInPlace(albumCustomPrint) && !inCart) {
      setConfigurationModalOpen(true);
      return;
    }

    onBundleToggle();
  };

  return (
    <div className={classes}>
      <div className="purchasing-option-item-thumb">
        <img width={75} src={getAsset('/combo.svg')} alt="" />
      </div>

      <div className="purchasing-option-item-content-wrap">
        <span className="purchasing-option-item-title">
          Digital Photo Pass + {albumCustomPrintName}
        </span>

        <div className="purchasing-option-item-info-extra">
          <Badge badgeStyle="yellow" className="text-extra-bold">
            <PriceDisplay
              title="Bundle price"
              price={finalPrice}
              oldPrice={oldPrice}
              showDiscount
              animatedStrikethrough
            />
          </Badge>
        </div>

        <div className="purchasing-option-item-content mb-2">
          {readOnly ? (
            <p className="text-sm lh-normal text-dark text-bold">
              <strong>All photos</strong> from {subscriptionItem.name} plus a
              custom print of your choice!
            </p>
          ) : (
            <p className="text-sm lh-normal text-dark text-bold">
              <strong>All {name} photos</strong> from {subscriptionItem.name}{' '}
              plus a custom print of your choice!
            </p>
          )}

          {albumCustomPrintHasPreview && (
            <p className="text-sm lh-normal text-dark text-bold">
              <Button
                btnStyle="link"
                className="text-sm lh-normal text-extra-bold"
                onClick={() => setPreviewModalOpen(true)}
              >
                See {albumCustomPrintName} sample
              </Button>
            </p>
          )}
        </div>

        {!readOnly && (
          <Button
            btnStyle={classNames({
              primary: !inCart,
              info: inCart,
            })}
            className={classNames({
              'purchasing-options-item-btn': true,
              'btn-purchase-options-in-cart': inCart,
            })}
            onClick={onBundleRequest}
            data-testid="btn-album-custom-print-bundle-subscription"
          >
            {inCart ? <AddedToCartLabel /> : <Fragment>Add to cart</Fragment>}
          </Button>
        )}

        {albumCustomPrintHasPreview && (
          <Fragment>
            <AlbumCustomPrintSubscriptionPreviewModal
              forceOpen={previewModalOpen}
              albumCustomPrint={albumCustomPrint}
              subscriptionItem={subscriptionItem}
              onBundleToggle={onBundleRequest}
              inCart={inCart}
              finalPrice={finalPrice}
              oldPrice={oldPrice}
              onClose={() => setPreviewModalOpen(false)}
            />
            <ImagePreload
              urls={[albumCustomPrint.custom_print.preview_image.url]}
            />
          </Fragment>
        )}

        <CustomPrintPlaqueConfiguratorModal
          forceOpen={configurationModalOPen}
          albumCustomPrint={albumCustomPrint}
          identity={identity}
          renderSubmit={config => {
            return (
              <Fragment>
                {validateCustomPrintPhotoSelections(config) && (
                  <Button
                    btnStyle="yellow"
                    btnSize="lg"
                    btnBlock
                    className={classNames({
                      'text-extra-bold': true,
                      'btn-purchase-options-in-cart': inCart,
                    })}
                    onClick={() => {
                      onBundleToggle(config);
                      setConfigurationModalOpen(false);
                    }}
                  >
                    <span className="pointer-events-none">
                      <Fragment>
                        Add to cart &ndash;{' '}
                        <PriceDisplay
                          price={finalPrice}
                          oldPrice={oldPrice}
                          showDiscount
                          animatedStrikethrough
                        />
                      </Fragment>
                    </span>
                  </Button>
                )}

                <Button
                  btnStyle="link"
                  btnBlock
                  onClick={() => setConfigurationModalOpen(false)}
                  className="text-center mt-2"
                >
                  Dismiss
                </Button>
              </Fragment>
            );
          }}
        />
      </div>
    </div>
  );
};

AlbumCustomPrintSubscriptionBundleOption.propTypes = propTypes;

export default AlbumCustomPrintSubscriptionBundleOption;
